import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import Markdown from "markdown-to-jsx";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowUpOutlined } from "@ant-design/icons";
import {
  sendMessageChat,
  getChats,
  getChatById,
  setFeedbackMessage,
  uploadFile,
} from "shared/service/StrmService";
import "./Quincy.scss";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { QuincyButtonClickEventName } from "event-tracker/button-click-event-name";
import ChatHistoryMenu from "./ChatHistoryMenu";
import { RouteComponentProps } from "react-router-dom";
import { ChatContextQuestionEnum } from "shared/enums/QuincyChatOriginEnum";
import RainbowEnergyRing from "./ai-thinking/RainbowEnergyRing";
import BlockInType from "./typing/BlockInType";
import { MotionShimmerCard } from "./shimmer-card/ShimmerCard";
import { Variants, motion } from "framer-motion";
import MessageActions from "./message-actions/MessageActions";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";
import { ReactComponent as BoltIcon } from "shared/assets/images/icon-bolt.svg";
import { ReactComponent as FileIcon } from "shared/assets/images/icon-file.svg";
import { ReactComponent as AudioIcon } from "shared/assets/images/icon-audio.svg";
import { Tag } from "antd";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";
import { useDispatch, useSelector } from "react-redux";
import {
  getQuincySubscriptionUsage,
  SubscriptionState,
} from "explorer/reducers/subscription";
import { FileContextNameEnum } from "shared/enums/FileContextName";
import chatLogoIcon from "shared/assets/images/chat-logo-icon.png";
import { UserTypeEnum } from "shared/enums/UserTypeEnum";
import { UserState } from "explorer/reducers/users";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

enum ChatPredefinedQuestionEnum {
  AboutMyCareerStage = 0,
  HowCanIGoToNextStage = 1,
  ExplainAboutAdvance = 2,
  Recommendations = 3,
  SocialMediaContentStrategy = 4,
  PlatformFocusStrategy = 5,
  StrategicPartners = 6,
  TimeManagementStrategy = 7,
  PreReleasePreparation = 8,
  CareerDifferentiation = 9,
  CareerStageErrors = 10,
  ReleasePerformanceAnalysis = 11,
}

enum ChatMessageFeedback {
  Like,
  Dislike,
}

interface Message {
  id: string;
  role: string;
  content: string;
  hide: boolean;
  feedback?: ChatMessageFeedback;
  isFromHistory?: boolean;
}

export interface QuincyMenuItem {
  key: string;
  label: string;
  createdOn: string;
  contextQuestion?: ChatContextQuestionEnum;
}

const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 50,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      duration: 0.8,
      bounce: 0.3,
    },
  },
};

const headerVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      type: "spring",
      bounce: 0.2,
    },
  },
};

const containerVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const chatTextareaVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      duration: 0.6,
      bounce: 0.2,
    },
  },
};

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const Quincy: React.FC<RouteComponentProps> = (props) => {
  const { match, history, location } = props;
  const {
    params: { id },
  }: any = match;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentPlan } = useSubscription();
  const { quincyUsage } = useSelector(
    (state: SubscriptionState) => state.subscription,
  );
  const lastMessageRef = useRef<null | HTMLDivElement>(null);
  const chatContainerRef = useRef<null | HTMLDivElement>(null);
  const inputTextRef = useRef<HTMLTextAreaElement>(null);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [completedMessages, setCompletedMessages] = useState<Set<string>>(
    new Set(),
  );
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [chatId, setChatId] = useState<string | null>(id);
  const [menuItems, setMenuItems] = useState<QuincyMenuItem[]>([]);
  const [outputTextRealtime, setOutputTextRealtime] = useState<Message | null>(
    null,
  );
  const [inputText, setInputText] = useState(
    (location.state as any)?.firstMessage,
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [temporaryUserMessages, setTemporaryUserMessages] = useState<Message[]>(
    [],
  );
  const [shouldLoadHistory, setShouldLoadHistory] = useState<boolean>(!!id);
  const [loading, setLoading] = useState<boolean>(false);
  const [historyFadeIn, setHistoryFadeIn] = useState<boolean>(!id);
  const [shuffledIndexes, setShuffledIndexes] = useState<number[]>([]);
  const [page, setPage] = useState<number>(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{
      uid: string;
      name: string;
      status: "uploading" | "done" | "error";
      progress?: number;
      url?: string;
      fileKey?: string;
      type?: string;
    }>
  >([]);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { loggedUser } = useSelector((state: UserState) => state.users);
  const adminUser = loggedUser.typeAccess === UserTypeEnum.AdminStrm;
  const allowFileUpload = adminUser;

  const disableGenerate = useMemo(() => {
    return (
      loading || (currentPlan === SubscriptionPlan.START && quincyUsage <= 0)
    );
  }, [loading, currentPlan, quincyUsage]);

  const shouldShowSugestion =
    currentPlan === SubscriptionPlan.START
      ? quincyUsage > 0 &&
        messages.length === 0 &&
        temporaryUserMessages.length === 0
      : messages.length === 0 && temporaryUserMessages.length === 0;

  const receivingAnswear = outputTextRealtime || isTyping;
  const hasChat = menuItems?.length > 0;
  const shouldOpenHistoryChat = chatHistoryOpen || !isMobile;
  const isSystemMessage = (role: string) => role !== "user";

  const sugestionQuestions = (() => {
    const enumValues = Object.values(ChatPredefinedQuestionEnum).filter(
      (v) => typeof v === "number",
    ) as number[];
    enumValues.sort((a, b) => a - b);
    return enumValues.map((val) => {
      const key = ChatPredefinedQuestionEnum[val] as unknown as string;
      return {
        text: t(`pages.Quincy.predefinedQuestions.${key}`),
        enumValue: val as ChatPredefinedQuestionEnum,
      };
    });
  })();

  const getChatHistory = useCallback(async () => {
    setLoading(chatId === null);
    const chats: any = await getChats();
    setMenuItems(
      chats?.response?.map((x: any) => {
        return {
          key: x.id,
          label: x.title,
          createdOn: x.createdOn,
          contextQuestion: x.contextQuestion,
        };
      }),
    );
    setLoading(false);
  }, [chatId]);

  const handleAutoScroll = useCallback((instant: boolean = false) => {
    if (chatContainerRef.current) {
      // Usa requestAnimationFrame para garantir que o DOM foi atualizado
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: instant ? "auto" : "smooth",
          });
        }
      });
    }
  }, []);

  // Scroll específico para acompanhar a digitação (instantâneo)
  const handleTypingScroll = useCallback(() => {
    if (chatContainerRef.current) {
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: "auto",
          });
        }
      });
    }
  }, []);

  const prepareForNewQuestion = (question: string) => {
    setSendDisabled(true);
    setInputText("");
    setOutputTextRealtime(null);
    const lastUserMessage = {
      role: "user",
      content: question,
      hide: false,
      id: `temp-user-${Date.now()}`,
    };

    return lastUserMessage;
  };

  const submitQuestion = async (
    question: string,
    predefinedQuestion?: ChatPredefinedQuestionEnum,
  ) => {
    if (sendDisabled || question === "" || disableGenerate) return false;
    setSendDisabled(true);
    setIsTyping(true);
    setInputText("");
    inputTextRef.current?.blur();
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_SEARCH, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    let newMessages = [...messages];
    try {
      if (outputTextRealtime) {
        newMessages.push(outputTextRealtime);
      }

      let previousMessages = newMessages.length > 0 ? [...newMessages] : null;
      const userMessage = prepareForNewQuestion(question);
      setTemporaryUserMessages([userMessage]);

      setTimeout(() => {
        handleAutoScroll();
      }, 100);

      const request = {
        previousMessages: previousMessages,
        predefinedQuestion: predefinedQuestion ?? null,
        prompt: question,
        chatId: chatId,
        files: uploadedFiles,
      };

      const answear: any = await sendMessageChat(request);
      const allMessages = answear.response.messages;
      dispatch(getQuincySubscriptionUsage());

      const updatedMessages = allMessages.map((msg: Message) => {
        const existingMessage = messages.find((m) => m.id === msg.id);
        return {
          ...msg,
          isFromHistory: existingMessage?.isFromHistory ?? false,
        };
      });

      setMessages(updatedMessages);
      setTemporaryUserMessages([]);
      setOutputTextRealtime(null);
      setIsTyping(false);
      await getChatHistory();
      setChatId(answear.response.id);

      setUploadedFiles([]);

      lastMessageRef?.current?.scrollIntoView();
      setSendDisabled(false);
    } catch (error) {
      console.error("Error during the request:", error);
      setIsTyping(false);
      setSendDisabled(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      submitQuestion(inputText);
    }
  };

  const updateMessageFeedbackById = (
    id: string,
    feedback?: ChatMessageFeedback,
  ) => {
    const newMessages = messages.map((item) => {
      if (item.id === id) {
        item.feedback = feedback;
      }
      return item;
    });

    if (outputTextRealtime?.id === id) {
      setOutputTextRealtime({
        ...outputTextRealtime,
        feedback: feedback,
      });
    } else {
      setMessages(newMessages);
    }
  };

  const onClickNewChat = async () => {
    setChatId(null);
    setMessages([]);
    setTemporaryUserMessages([]);
    setOutputTextRealtime(null);
    setChatHistoryOpen(false);
    setSendDisabled(false);
    setHistoryFadeIn(true);
  };

  const onClickMenuItem = async (info: any) => {
    setShouldLoadHistory(true);
    setChatId(info.key);
    setSendDisabled(false);
    history.push(`/quincy/${info.key}`);
  };

  const onClickLikeMessage = async (messageId: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_LIKE, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    updateMessageFeedbackById(messageId, ChatMessageFeedback.Like);
    await setFeedbackMessage(messageId, ChatMessageFeedback.Like);
  };

  const onClickDislikeMessage = async (messageId: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_DISLIKE, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    updateMessageFeedbackById(messageId, ChatMessageFeedback.Dislike);
    await setFeedbackMessage(messageId, ChatMessageFeedback.Dislike);
  };

  const onClickCopyMessage = (text: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_COPY, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success<string>(t("pages.Quincy.textCopied"));
      })
      .catch((error) => {
        console.error("Falha ao copiar o texto:", error);
      });
  };

  const handleMessageCompleteCallback = useCallback((messageId: string) => {
    setCompletedMessages((prev) => new Set([...prev, messageId]));
  }, []);

  function shuffleArray(arr: number[]) {
    return arr.sort(() => Math.random() - 0.5);
  }

  // allowed file type prefixes (single source of truth)
  const allowedTypePrefixes = ["image/", "audio/"];

  const isAllowedFile = (file: File) =>
    allowedTypePrefixes.some((prefix) => file.type.startsWith(prefix));

  const normalizeFileType = (type?: string) => {
    if (!type) return undefined;
    if (type.startsWith("image/")) return "image";
    if (type.startsWith("audio/")) return "audio";
    return type;
  };

  const openFilePicker = (accept: string) => {
    if (!allowFileUpload) return;
    if (!fileInputRef.current) return;
    fileInputRef.current.accept = accept;
    fileInputRef.current.click();
    setShowUploadOptions(false);
  };

  const uploadSingleFile = async (file: File) => {
    if (!allowFileUpload) {
      toast.error("File upload is disabled");
      return;
    }

    const uid = `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setUploadedFiles((prev) => [
      ...prev,
      { uid, name: file.name, status: "uploading", progress: 0 },
    ]);

    try {
      const form = new FormData();
      form.append("file", file, (file && (file as any).name) || "file");
      const response: any = await uploadFile({
        contextName: FileContextNameEnum.Chat,
        key: chatId ?? "quincy",
        file: form,
        options: {
          onUploadProgress: (ev: any) => {
            const percent = ev.total
              ? Math.round((ev.loaded * 100) / ev.total)
              : 0;
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.uid === uid ? { ...f, progress: percent } : f,
              ),
            );
          },
        },
      });

      if (response) {
        const normalizedType = normalizeFileType(file.type);
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.uid === uid
              ? { ...f, status: "done", ...response, type: normalizedType }
              : f,
          ),
        );
      }
    } catch (error) {
      console.error("Upload failed", error);
      setUploadedFiles((prev) =>
        prev.map((f) => (f.name === file.name ? { ...f, status: "error" } : f)),
      );
    }
  };

  const handleFileInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (!allowFileUpload) return;
    const files = ev.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      if (isAllowedFile(file)) {
        uploadSingleFile(file);
      } else {
        toast.error("Only images or audio files are allowed");
      }
    });

    ev.currentTarget.value = "";
  };

  const removeUploadedFile = (uid: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.uid !== uid));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!allowFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!allowFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!allowFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!allowFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      if (isAllowedFile(file)) {
        uploadSingleFile(file);
      } else {
        toast.error("Only images or audio files are allowed");
      }
    });
  };

  useEffect(() => {
    const allIndexes = Array.from(
      { length: sugestionQuestions.length },
      (_, i) => i,
    );
    setShuffledIndexes(shuffleArray(allIndexes));
    setPage(0);
  }, [sugestionQuestions.length]);

  const handleShowMoreSuggestions = () => {
    const totalPages = Math.ceil(sugestionQuestions.length / 4);
    if (page + 1 < totalPages) {
      setPage(page + 1);
    } else {
      const allIndexes = Array.from(
        { length: sugestionQuestions.length },
        (_, i) => i,
      );
      setShuffledIndexes(shuffleArray(allIndexes));
      setPage(0);
    }
  };

  useEffect(() => {
    if (inputTextRef.current) {
      inputTextRef.current.focus();
      inputTextRef.current.setSelectionRange(
        inputTextRef.current.value?.length,
        inputTextRef.current.value?.length,
      );
    }
  }, []);

  useEffect(() => {
    getChatHistory();
  }, [getChatHistory]);

  useEffect(() => {
    const getMessages = async () => {
      setLoading(true);
      setHistoryFadeIn(false);
      const chatMessages: any = await getChatById(chatId!);
      setChatId(chatId);

      const messagesWithHistoryFlag = (chatMessages?.response ?? []).map(
        (msg: Message) => ({
          ...msg,
          isFromHistory: true,
        }),
      );
      setMessages(messagesWithHistoryFlag);
      setTemporaryUserMessages([]);
      setOutputTextRealtime(null);
      setChatHistoryOpen(false);
      setLoading(false);

      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }

        setTimeout(() => {
          setHistoryFadeIn(true);
        }, 50);
      }, 100);
    };

    if (chatId && (messages.length === 0 || shouldLoadHistory)) {
      getMessages();
      setShouldLoadHistory(false);
    }
  }, [chatId, shouldLoadHistory, messages.length]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
  }, []);

  useEffect(() => {
    if (isTyping) {
      setTimeout(() => {
        handleAutoScroll();
      }, 300);
    }
  }, [isTyping, handleAutoScroll]);

  useEffect(() => {
    dispatch(getQuincySubscriptionUsage());
  }, [dispatch]);

  useEffect(() => {
    if (currentPlan === SubscriptionPlan.START && quincyUsage <= 0) {
      console.log("User is on START plan and has no usage left");
      setSendDisabled(true);
    }
  }, [quincyUsage, currentPlan]);

  useEffect(() => {
    if (!allowFileUpload) {
      setUploadedFiles([]);
      setShowUploadOptions(false);
      setDragActive(false);
    }
  }, [allowFileUpload]);

  return (
    <div
      className={`page-quincy ${isMobile && "mobile"} ${
        shouldOpenHistoryChat && "chat-history-open"
      }`}
    >
      <ChatHistoryMenu
        menuItems={menuItems}
        chatIdSelected={chatId}
        onClickMenuItem={onClickMenuItem}
        onClickNewChat={onClickNewChat}
        loading={loading}
        hide={chatHistoryOpen}
        onClose={() => setChatHistoryOpen(false)}
        hasChat={hasChat}
      />
      <div className="content">
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SubscriptionDefaultCTA />
        </div>
        <motion.div
          className="header"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 className="title" variants={headerVariants}>
            <>
              {t("pages.Quincy.quincy")}{" "}
              {currentPlan === SubscriptionPlan.START && (
                <Tag className="tag-credits">
                  <BoltIcon fill="#f9b3de" />
                  {quincyUsage ? `Credits: ${quincyUsage}` : "Back in 24h"}
                </Tag>
              )}
            </>
          </motion.h1>
          <motion.h3 className="subtitle" variants={headerVariants}>
            {t("pages.Quincy.title")}
          </motion.h3>
        </motion.div>
        <div className={`chat-wrapper ${isMobile && "mobile"}`}>
          <div
            ref={chatContainerRef}
            className={`chat-container scroll-style ${
              !historyFadeIn && messages.length > 0 ? "history-loading" : ""
            }`}
            style={{ marginBottom: isMobile ? 0 : 100 }}
          >
            {[...messages, ...temporaryUserMessages]
              .filter((x) => !x.hide)
              .map((ev) => (
                <motion.div
                  key={
                    ev.role === "user" && ev.id.startsWith("temp-")
                      ? `${ev.id}-${ev.content}`
                      : ev.id
                  }
                  className={
                    isSystemMessage(ev.role) ? "message-system" : "message-user"
                  }
                  initial={
                    ev.role === "user" && ev.id.startsWith("temp-")
                      ? { y: 20, opacity: 0 }
                      : false
                  }
                  animate={
                    ev.role === "user" && ev.id.startsWith("temp-")
                      ? { y: 0, opacity: 1 }
                      : false
                  }
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  {isSystemMessage(ev.role) ? (
                    <>
                      <BlockInType
                        key={ev.id}
                        fullText={ev.content}
                        onComplete={() => handleMessageCompleteCallback(ev.id)}
                        onTextChange={handleTypingScroll}
                        skipAnimation={ev.isFromHistory === true}
                      />
                      <MessageActions
                        ref={lastMessageRef}
                        message={ev}
                        isMessageComplete={completedMessages.has(ev.id)}
                        onLike={onClickLikeMessage}
                        onDislike={onClickDislikeMessage}
                        onCopy={onClickCopyMessage}
                      />
                    </>
                  ) : (
                    <Markdown>{ev.content}</Markdown>
                  )}
                </motion.div>
              ))}
            {receivingAnswear && (
              <div className={`message-system`}>
                <div className="message-icon"></div>
                <div className="paragraph">
                  {outputTextRealtime ? (
                    <BlockInType
                      key={outputTextRealtime.id}
                      fullText={outputTextRealtime.content}
                      minSpeed={15}
                      maxSpeed={35}
                      onComplete={() => {
                        setSendDisabled(false);
                        handleMessageCompleteCallback(outputTextRealtime.id);
                      }}
                      onTextChange={handleTypingScroll}
                    />
                  ) : isTyping ? (
                    <div className="thinking-indicator-wrapper">
                      <RainbowEnergyRing
                        isActive={true}
                        size={25}
                        showText={true}
                        textPosition="right"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          {shouldShowSugestion && (
            <div className="sugestion-wrapper">
              <img
                src={chatLogoIcon}
                alt="chatLogoIcon"
                className="chat-logo-icon"
                style={{ display: isMobile ? "none" : "none" }}
              />

              <div className={`show-more-btn-wrapper ${isMobile && "mobile"}`}>
                <button
                  className="show-more-btn"
                  onClick={handleShowMoreSuggestions}
                >
                  {t("pages.Quincy.showMoreSuggestions")}
                </button>
              </div>
              <div className="container-grid">
                <motion.div
                  className="suggestions-grid"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {shuffledIndexes.slice(page * 4, page * 4 + 4).map((idx) => {
                    const question = sugestionQuestions[idx];
                    return (
                      <MotionShimmerCard
                        key={idx}
                        text={question.text}
                        enumValue={question.enumValue}
                        onClick={(text, enumValue) =>
                          submitQuestion(
                            text || "",
                            enumValue as ChatPredefinedQuestionEnum,
                          )
                        }
                        isMobile={isMobile}
                        variant="compact"
                        variants={cardVariants}
                      />
                    );
                  })}
                </motion.div>
              </div>
            </div>
          )}
          <div className={`search-section ${isMobile && "mobile"}`}>
            <motion.div
              className={`chat-textarea-wrapper ${isMobile && "mobile"} ${
                dragActive ? "drag-active" : ""
              }`}
              variants={chatTextareaVariants}
              initial="hidden"
              animate="visible"
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {dragActive && (
                <div className="drop-overlay" aria-hidden>
                  Solte os arquivos aqui para enviar
                </div>
              )}
              <div className="upload-container">
                {uploadedFiles.length > 0 && (
                  <div className="uploaded-list">
                    {uploadedFiles.map((f) => (
                      <div className="file-card" key={f.uid}>
                        <div className="file-icon">
                          <FileIcon stroke="#CECDFF" />
                        </div>
                        <div className="file-meta">
                          <div>
                            <span className="file-name">{f.name}</span>
                            <span className="file-type">
                              {capitalizeFirstLetter(f.type || "")}
                            </span>
                          </div>
                          {f.status === "uploading" && (
                            <div className="file-progress">
                              <div
                                className="file-progress-bar"
                                style={{ width: `${f.progress ?? 0}%` }}
                              />
                            </div>
                          )}
                        </div>
                        <button
                          className="file-remove"
                          onClick={() => removeUploadedFile(f.uid)}
                          aria-label={`remove-${f.name}`}
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ position: "relative", width: "100%" }}>
                {allowFileUpload && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={handleFileInputChange}
                    />
                    <div
                      className={`upload-trigger ${uploadedFiles.length > 0 ? "has-files" : ""}`}
                      onClick={() =>
                        allowFileUpload && setShowUploadOptions((s) => !s)
                      }
                      aria-label="upload"
                      role="button"
                    >
                      <span className="plus">
                        <FontAwesomeIcon icon={faPlus} />
                      </span>
                    </div>
                  </>
                )}

                {allowFileUpload && showUploadOptions && (
                  <div className="upload-options">
                    <button
                      type="button"
                      onClick={() => openFilePicker("*/*")}
                      className="upload-opt"
                    >
                      <FileIcon stroke="#CECDFF" /> Upload file
                    </button>
                    <button
                      type="button"
                      onClick={() => openFilePicker("audio/*")}
                      className="upload-opt"
                    >
                      <AudioIcon stroke="#CECDFF" /> Upload Audio File
                    </button>
                  </div>
                )}

                <textarea
                  id="chat-textarea"
                  placeholder={
                    receivingAnswear ? "" : t("pages.Quincy.placeholder")
                  }
                  ref={inputTextRef}
                  onChange={(ev) => setInputText(ev.target.value)}
                  value={inputText}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    if (sendDisabled) {
                      inputTextRef.current?.blur();
                      return;
                    }

                    if (inputTextRef.current) {
                      const length = inputTextRef.current.value?.length ?? 0;
                      inputTextRef.current.setSelectionRange(length, length);
                    }
                  }}
                  style={{
                    padding: allowFileUpload
                      ? isMobile
                        ? "17px 60px 10px 70px"
                        : "26px 60px 10px 70px"
                      : "10px 60px 10px 15px",
                  }}
                />

                <button
                  className={`search-btn ${
                    (sendDisabled || inputText === "") && "icon-disabled"
                  }`}
                  onClick={() => submitQuestion(inputText)}
                  disabled={sendDisabled || inputText === "" || disableGenerate}
                  style={{
                    cursor: disableGenerate ? "not-allowed" : "pointer",
                  }}
                >
                  <ArrowUpOutlined />
                </button>
              </div>
            </motion.div>

            <p className={`disclaimer ${isMobile && "mobile"}`}>
              {t("pages.Quincy.disclaimer")}
            </p>
          </div>
        </div>
        {/* Substituindo Form.Item e Input do Ant Design */}
      </div>
    </div>
  );
};

export default Quincy;
